import { Label, QuantityStepperControls } from 'components/CreateOrderForm';
import styles from './OrderAgreement.module.scss';
import { Group, NumberInput, NumberInputHandlers, Switch } from '@mantine/core';
import { ReactComponent as InfoIcon } from './img/iconInfo.svg';
import { ReactComponent as IconCalculator } from './img/iconCalculator.svg';
import { Button } from 'ui/Button';
import { useEffect, useRef, useState } from 'react';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { ValueOrderAgreement } from 'redux/Api/ordersApi/type';
import {
  OrderAmountErrorMessage,
  useOrderAmountError,
} from 'components/OrderAmountError';
import { SelectedWorker } from 'components/CreateOrderForm/types';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useMediaQuery } from '@mantine/hooks';
import { ReactComponent as IconRuble } from './img/iconRuble.svg';
import { AppTooltip } from 'ui/AppTooltip';
import Bunner from 'ui/Bunner';

interface OrderAgreementProps {
  disableAgreement: boolean;
  valueOrderAgreement: ValueOrderAgreement;
  setValueOrderAgreement: (e: ValueOrderAgreement) => void;
  textErrorValueOrderAgreement: string | null;
  amountErrorOrderAgreement?: string | null;
  clearAmountErrorOrderAgreement?: () => void;
  selectedWorker?: SelectedWorker | null;
  setDisableAgreementButton?: (disableAgreementButton: boolean) => void;
  contractLimit?: number | null;
  showBanner?: boolean;
}

export const OrderAgreement = ({
  disableAgreement,
  valueOrderAgreement,
  setValueOrderAgreement,
  textErrorValueOrderAgreement,
  amountErrorOrderAgreement = null,
  clearAmountErrorOrderAgreement,
  selectedWorker = null,
  setDisableAgreementButton,
  contractLimit,
  showBanner = false,
}: OrderAgreementProps) => {
  const { data: profileData } = useGetProfileQuery();
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const [commission, setCommission] = useState(0);
  const unitHandlersRef = useRef<NumberInputHandlers>(null);
  const amountHandlersRef = useRef<NumberInputHandlers>(null);
  const isVolumeFocusedRef = useRef(false);

  const orderAmountInput = {
    unitPrice: +valueOrderAgreement.unit_price,
    totalPrice: +valueOrderAgreement.amount,
    unitVolume: valueOrderAgreement.unit_volume,
    useFastWithdraw: !!infoOrderDetail.use_fast_withdraw,
    selectedWorker,
    contractLimit,
    useTotalPriceOnly: true,
  };

  const widthIconCalculator = useMediaQuery('(width < 1400px) and (width > 1200px)');

  const { message: orderAmountMessage } = useOrderAmountError(orderAmountInput);

  useEffect(() => {
    if (orderAmountMessage) {
      setDisableAgreementButton?.(true);
    } else {
      setDisableAgreementButton?.(false);
    }
  }, [orderAmountMessage]);

  const handleTogglePercent = () => {
    const currentAmount = +valueOrderAgreement.amount;
    const isTaxIncluded = valueOrderAgreement.include_tax_amount === 1;

    const roundedAmount = isTaxIncluded
      ? Math.round((currentAmount / (1 + 0.06 / 0.94)) * 100) / 100
      : Math.round(currentAmount * (1 + 0.06 / 0.94) * 100) / 100;

    setValueOrderAgreement({
      ...valueOrderAgreement,
      amount: roundedAmount,
      include_tax_amount: isTaxIncluded ? 0 : 1,
    });
  };

  useEffect(() => {
    const commission =
      (valueOrderAgreement.amount * (profileData?.commission?.percent ?? 0)) / 100;
    if (commission < (profileData?.commission?.min_amount ?? 0)) {
      setCommission(profileData?.commission?.min_amount ?? 0);
    } else {
      setCommission(Number(commission.toFixed(2)));
    }
  }, [valueOrderAgreement.amount]);

  return (
    <div className={styles.documents}>
      <div className={styles.documents_title}>
        <p>Подтверждение объема</p>
      </div>
      {showBanner && (
        <Bunner
          title='Внимание!'
          description='Данный объем указал исполнитель, перед подтверждением убедись, что он верный. Указанный объем можно изменить'
        />
      )}
      <div>
        <div
          className={`${styles.amountWrapper} ${
            widthIconCalculator ? styles.amountWrapperWithIcon : ''
          }`}>
          <div className={styles.fieldCell}>
            <Label text='Объем' />
            <NumberInput
              flex={1}
              placeholder='1'
              allowNegative={false}
              disabled={disableAgreement}
              min={1}
              startValue={1}
              decimalScale={0}
              hideControls
              handlersRef={unitHandlersRef}
              value={valueOrderAgreement.unit_volume || ''}
              rightSectionPointerEvents='auto'
              rightSection={
                <QuantityStepperControls
                  onIncrement={() => unitHandlersRef.current?.increment()}
                  onDecrement={() => unitHandlersRef.current?.decrement()}
                  decrementDisabled={Number(valueOrderAgreement.unit_volume || 1) <= 1}
                />
              }
              onFocus={() => {
                isVolumeFocusedRef.current = true;
              }}
              onChange={(val) => {
                const unit_volume =
                  val === '' || val === null
                    ? isVolumeFocusedRef.current
                      ? 0
                      : 1
                    : Number(val);

                setValueOrderAgreement({
                  ...valueOrderAgreement,
                  unit_volume,
                });
              }}
              onBlur={() => {
                isVolumeFocusedRef.current = false;

                if (!valueOrderAgreement.unit_volume) {
                  setValueOrderAgreement({
                    ...valueOrderAgreement,
                    unit_volume: 1,
                  });
                }
              }}
              styles={{
                input: {
                  height: '46px',
                  fontSize: '16px',
                  border: 'none',
                },
                error: { fontSize: '14px', color: '#e10d0d' },
              }}
            />
          </div>

          <div className={styles.fieldCell}>
            <Label text='Итоговая стоимость' />
            <NumberInput
              flex={1}
              placeholder='1'
              disabled={disableAgreement}
              allowNegative={false}
              decimalScale={2}
              hideControls
              handlersRef={amountHandlersRef}
              rightSectionPointerEvents='auto'
              value={valueOrderAgreement.amount || ''}
              onChange={(val) => {
                clearAmountErrorOrderAgreement?.();
                setValueOrderAgreement({
                  ...valueOrderAgreement,
                  amount: val === '' || val === null ? 0 : Number(val),
                  include_tax_amount: 0,
                });
              }}
              rightSection={<IconRuble />}
              styles={{
                input: {
                  height: '46px',
                  fontSize: '16px',
                  border:
                    amountErrorOrderAgreement ||
                    (orderAmountMessage &&
                      orderAmountInput.totalPrice > 0 &&
                      !disableAgreement)
                      ? '1px solid red'
                      : 'none',
                },
                error: { fontSize: '14px', color: '#e10d0d' },
              }}
            />
          </div>

          {!disableAgreement && (
            <div className={styles.errorCell}>
              {textErrorValueOrderAgreement && (
                <p className={styles.error_value}>{textErrorValueOrderAgreement}</p>
              )}
            </div>
          )}

          {!disableAgreement && (
            <div className={`${styles.errorCell} ${styles.errorCell_price}`}>
              {amountErrorOrderAgreement ? (
                <p className={styles.error_price}>{amountErrorOrderAgreement}</p>
              ) : (
                orderAmountMessage &&
                orderAmountInput.totalPrice > 0 && (
                  <OrderAmountErrorMessage
                    message={orderAmountMessage}
                    className={styles.error_price}
                  />
                )
              )}
            </div>
          )}

          {!disableAgreement ? (
            <div className={styles.blockAmount}>
              <Button
                theme='link'
                className={`${styles.buttonRecalculate} ${
                  widthIconCalculator ? styles.buttonRecalculateIcon : ''
                }`}
                onClick={() => {
                  setValueOrderAgreement({
                    ...valueOrderAgreement,
                    amount:
                      valueOrderAgreement.unit_volume * valueOrderAgreement.unit_price,
                    include_tax_amount: 0,
                  });
                }}
                disabled={disableAgreement}>
                {!widthIconCalculator ? (
                  'Пересчитать'
                ) : (
                  <IconCalculator className={styles.calculatorIcon} />
                )}
              </Button>
            </div>
          ) : (
            <div
              className={
                widthIconCalculator ? styles.amountSpacerIcon : styles.amountSpacer
              }
            />
          )}
        </div>
      </div>
      <>
        <Group gap='8px'>
          <Switch
            color='#002CFB'
            styles={{
              label: {
                fontSize: '16px',
                lineHeight: '22px',
                color: '#121922',
                cursor: disableAgreement ? 'not-allowed' : 'pointer',
              },
              track: {
                border: 'none',
                cursor: disableAgreement ? 'not-allowed' : 'pointer',
                backgroundColor: valueOrderAgreement.include_tax_amount
                  ? disableAgreement
                    ? '#e1e6fe'
                    : '#002CFB'
                  : 'rgba(173, 191, 223, 0.40)',
              },
              thumb: {
                border: 'none',
              },
            }}
            checked={valueOrderAgreement.include_tax_amount === 1}
            disabled={disableAgreement}
            onChange={() => {
              handleTogglePercent();
            }}
            label='Добавить 6%'
          />
          <AppTooltip
            label={
              valueOrderAgreement.amount
                ? `Мы добавим такую сумму, которая после выплаты 6% налога, составит ${valueOrderAgreement.amount} ₽`
                : `Мы добавим такую сумму, которая после выплаты 6% налога составит введенную`
            }>
            <InfoIcon style={{ cursor: 'pointer' }} />
          </AppTooltip>
        </Group>
        <Group gap='8px' mt='4px' style={{ cursor: 'pointer' }}>
          <Switch
            color='#002CFB'
            styles={{
              label: {
                fontSize: '16px',
                lineHeight: '22px',
                color: '#121922',
                cursor: disableAgreement ? 'not-allowed' : 'pointer',
              },
              track: {
                border: 'none',
                cursor: disableAgreement ? 'not-allowed' : 'pointer',
                backgroundColor: valueOrderAgreement.accept_automatically
                  ? disableAgreement
                    ? '#e1e6fe'
                    : '#002CFB'
                  : 'rgba(173, 191, 223, 0.40)',
              },
              thumb: {
                border: 'none',
              },
            }}
            checked={valueOrderAgreement.accept_automatically === 1}
            disabled={disableAgreement}
            onChange={(event) => {
              setValueOrderAgreement({
                ...valueOrderAgreement,
                accept_automatically: event.currentTarget.checked ? 1 : 0,
              });
            }}
            label='Завершить заказ автоматически'
          />
          <AppTooltip
            label={
              'После завершения заказа исполнителем автоматически будет подписан акт и отправлена выплата'
            }>
            <InfoIcon style={{ cursor: 'pointer' }} />
          </AppTooltip>
        </Group>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div className={styles.order_inner__amount}>
            <p>{addSpaceNumber(valueOrderAgreement.amount || 0)} ₽</p>
          </div>
          <p>Комиссия {addSpaceNumber(commission)} ₽</p>
        </div>
      </>
    </div>
  );
};
